import React, { useEffect, useId, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import StarIcon from '@mui/icons-material/Star';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { StepChartWithAverage } from '../../components';
import type { TrainingEntry } from '../../app/feathers/users/User';
import { useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';

interface PerformanceColumnProps {
  list: TrainingEntry[];
  trainingLevel?: number | null;
  trainingDataByLevel?: Record<number, TrainingEntry[]>;
}

const PREVIEW_POINTS = 15;
const CANDIDATE_FETCH_CHUNK_SIZE = 20;
const TIME_PURPLE = '#22034f';
const QUALITY_PURPLE = '#876ce9';

interface ChartPoint {
  x: number;
  y: number;
  candidateId: string;
  completedAt: string;
}

interface DetailedStepChartProps {
  title: string;
  points: ChartPoint[];
  color: string;
  valueLabel: (y: number) => string;
  yDomain?: { min: number; max: number };
  yTickValues?: number[];
  tooltipMode?: 'time' | 'quality';
  candidateById: Record<string, { _id: string; name?: string }>;
  average: number | null;
}

const DetailedStepChart: React.FC<DetailedStepChartProps> = ({
  title,
  points,
  color,
  valueLabel,
  yDomain,
  yTickValues,
  tooltipMode = 'time',
  candidateById,
  average,
}) => {
  const [hoverLineX, setHoverLineX] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{ clientX: number; clientY: number; point: ChartPoint } | null>(null);
  const gradientBaseId = useId().replace(/:/g, '');
  const width = 760;
  const height = 220;
  const margin = { top: 20, right: 24, bottom: 32, left: 56 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = yDomain ? yDomain.min : Math.min(0, ...ys);
  const maxY = yDomain ? yDomain.max : Math.max(0, ...ys);
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;

  const toSvg = (px: number, py: number) => ({
    x: margin.left + ((px - minX) / rangeX) * plotWidth,
    y: margin.top + plotHeight - ((py - minY) / rangeY) * plotHeight,
  });

  const stepPath = points.reduce((acc, point, i) => {
    const { x, y } = toSvg(point.x, point.y);
    if (i === 0) return `M ${x} ${y}`;
    const prev = toSvg(points[i - 1].x, points[i - 1].y);
    return `${acc} L ${x} ${prev.y} L ${x} ${y}`;
  }, '');

  const computedYTicks = yTickValues ?? Array.from({ length: 5 }, (_, i) => minY + (i / 4) * (maxY - minY));
  const zeroLineY = toSvg(minX, 0).y;
  const firstX = toSvg(points[0].x, points[0].y).x;
  const lastX = toSvg(points[points.length - 1].x, points[points.length - 1].y).x;
  const bottomY = margin.top + plotHeight;
  const areaPath = `${stepPath} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
  const gradientId = `detail-step-gradient-${gradientBaseId}-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const scaleX = rect.width > 0 ? width / rect.width : 1;
    const xInViewBox = (event.clientX - rect.left) * scaleX;
    const relativeX = Math.max(0, Math.min(plotWidth, xInViewBox - margin.left));
    const cursorSvgX = margin.left + relativeX;
    let stepIndex = 0;
    for (let i = 1; i < points.length; i++) {
      const stepBoundaryX = toSvg(points[i].x, points[i].y).x;
      if (cursorSvgX >= stepBoundaryX) stepIndex = i;
      else break;
    }
    const activePoint = points[stepIndex];
    setHoverLineX(cursorSvgX);
    setTooltip({
      clientX: event.clientX,
      clientY: event.clientY,
      point: activePoint,
    });
  };

  const handleMouseLeave = () => {
    setHoverLineX(null);
    setTooltip(null);
  };

  return (
    <Box sx={{ mt: 2, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {average != null && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Avg (last 15):
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color }}>
              {valueLabel(average)}
            </Typography>
            {tooltipMode === 'quality' && <StarIcon sx={{ fontSize: 16, color: QUALITY_PURPLE }} />}
          </Box>
        )}
      </Box>
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${title} chart`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity={0.32} />
            <stop offset="100%" stopColor={color} stopOpacity={0.03} />
          </linearGradient>
        </defs>
        {computedYTicks.map((value) => {
          const { y } = toSvg(minX, value);
          return (
            <g key={`y-${value}`}>
              <text
                x={margin.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill="rgba(0, 0, 0, 0.7)"
              >
                {valueLabel(value)}
              </text>
            </g>
          );
        })}
        <line
          x1={margin.left}
          y1={zeroLineY}
          x2={width - margin.right}
          y2={zeroLineY}
          stroke="rgba(0, 0, 0, 0.28)"
          strokeWidth={1.2}
        />
        {hoverLineX != null && (
          <line
            x1={hoverLineX}
            y1={margin.top}
            x2={hoverLineX}
            y2={height - margin.bottom}
            stroke="rgba(0, 0, 0, 0.18)"
            strokeWidth={1}
          />
        )}
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path d={stepPath} fill="none" stroke={color} strokeWidth={2} strokeLinecap="square" />
      </svg>
      {tooltip && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.clientX + 12,
            top: tooltip.clientY - 8,
            bgcolor: '#fff',
            color: '#111827',
            px: 1.25,
            py: 1,
            borderRadius: 1.25,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
            pointerEvents: 'none',
            zIndex: 1500,
            minWidth: 180,
          }}
        >
          {tooltipMode === 'quality' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {tooltip.point.y.toFixed(1)}
              </Typography>
              <StarIcon sx={{ fontSize: 16, color: QUALITY_PURPLE }} />
            </Box>
          ) : (
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {valueLabel(tooltip.point.y)}
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', mt: 0.25 }}>
            {candidateById[tooltip.point.candidateId]?.name || tooltip.point.candidateId}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {tooltip.point.completedAt
              ? new Date(tooltip.point.completedAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })
              : 'Unknown submit time'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export const PerformanceColumn: React.FC<PerformanceColumnProps> = ({
  list,
  trainingLevel,
  trainingDataByLevel,
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [detailOpen, setDetailOpen] = useState(false);
  const [candidateById, setCandidateById] = useState<Record<string, { _id: string; name?: string }>>({});
  const [selectedLevel, setSelectedLevel] = useState<number | null>(trainingLevel ?? null);

  const timePoints = list
    .map((e, i) => (
      e.timeSpentMinutes != null
        ? { x: i + 1, y: e.timeSpentMinutes, candidateId: e.candidateId, completedAt: e.completedAt }
        : null
    ))
    .filter((p): p is ChartPoint => p != null);
  const qualityPoints = list
    .map((e, i) => (
      e.reviewScore != null
        ? { x: i + 1, y: e.reviewScore, candidateId: e.candidateId, completedAt: e.completedAt }
        : null
    ))
    .filter((p): p is ChartPoint => p != null);

  const previewTimePoints = timePoints.slice(-PREVIEW_POINTS);
  const previewQualityPoints = qualityPoints.slice(-PREVIEW_POINTS);
  const previewTimeAvg =
    previewTimePoints.length > 0
      ? previewTimePoints.reduce((sum, point) => sum + point.y, 0) / previewTimePoints.length
      : null;
  const previewQualityAvg =
    previewQualityPoints.length > 0
      ? previewQualityPoints.reduce((sum, point) => sum + point.y, 0) / previewQualityPoints.length
      : null;
  const isTimeAverageHigh = (previewTimeAvg ?? 0) > 20;

  const availableLevels = useMemo(() => {
    const levels = new Set<number>();
    Object.entries(trainingDataByLevel || {}).forEach(([levelKey, levelEntries]) => {
      const parsed = Number(levelKey);
      if (Number.isFinite(parsed) && Array.isArray(levelEntries) && levelEntries.length > 0) {
        levels.add(parsed);
      }
    });
    if (trainingLevel != null) {
      levels.add(trainingLevel);
    }
    return Array.from(levels).sort((a, b) => a - b);
  }, [trainingDataByLevel, trainingLevel]);

  const activeLevel = selectedLevel ?? trainingLevel ?? availableLevels[0] ?? null;
  const detailList =
    activeLevel != null
      ? (trainingDataByLevel?.[activeLevel] ?? list)
      : list;

  const detailTimePoints = detailList
    .map((e, i) => (
      e.timeSpentMinutes != null
        ? { x: i + 1, y: e.timeSpentMinutes, candidateId: e.candidateId, completedAt: e.completedAt }
        : null
    ))
    .filter((p): p is ChartPoint => p != null);
  const detailQualityPoints = detailList
    .map((e, i) => (
      e.reviewScore != null
        ? { x: i + 1, y: e.reviewScore, candidateId: e.candidateId, completedAt: e.completedAt }
        : null
    ))
    .filter((p): p is ChartPoint => p != null);

  const detailTimePointsForAvg = detailTimePoints.slice(-PREVIEW_POINTS);
  const detailQualityPointsForAvg = detailQualityPoints.slice(-PREVIEW_POINTS);
  const detailTimeAvg =
    detailTimePointsForAvg.length > 0
      ? detailTimePointsForAvg.reduce((sum, point) => sum + point.y, 0) / detailTimePointsForAvg.length
      : null;
  const detailQualityAvg =
    detailQualityPointsForAvg.length > 0
      ? detailQualityPointsForAvg.reduce((sum, point) => sum + point.y, 0) / detailQualityPointsForAvg.length
      : null;

  const candidateIdsInChart = useMemo(
    () =>
      Array.from(
        new Set([...detailTimePoints, ...detailQualityPoints].map((point) => point.candidateId).filter(Boolean))
      ),
    [detailTimePoints, detailQualityPoints]
  );

  useEffect(() => {
    if (!feathers || !detailOpen || candidateIdsInChart.length === 0) return;
    const missingCandidateIds = candidateIdsInChart.filter((candidateId) => !candidateById[candidateId]);
    if (missingCandidateIds.length === 0) return;

    let cancelled = false;
    const fetchMissingCandidates = async () => {
      try {
        const chunks = Array.from(
          { length: Math.ceil(missingCandidateIds.length / CANDIDATE_FETCH_CHUNK_SIZE) },
          (_, i) => missingCandidateIds.slice(i * CANDIDATE_FETCH_CHUNK_SIZE, (i + 1) * CANDIDATE_FETCH_CHUNK_SIZE)
        );

        const results = await Promise.all(
          chunks.map((chunkIds) =>
            feathers.getService('candidates').find({
              query: {
                _id: { $in: chunkIds },
                $limit: chunkIds.length,
                $select: ['_id', 'name'],
              },
            })
          )
        );

        if (cancelled) return;
        const fetchedCandidates = results.flatMap((result) => ((result as { data?: unknown[] }).data || []));
        const fetchedById = fetchedCandidates.reduce(
          (acc: Record<string, { _id: string; name?: string }>, candidate: unknown) => {
            const candidateRecord = candidate as { _id?: string; name?: string };
            if (candidateRecord?._id) {
              acc[candidateRecord._id] = { _id: candidateRecord._id, name: candidateRecord.name };
            }
            return acc;
          },
          {}
        );
        setCandidateById((prev) => ({ ...prev, ...fetchedById }));
      } catch (err) {
        console.error('Failed to fetch candidates for performance detail', err);
      }
    };

    fetchMissingCandidates();
    return () => {
      cancelled = true;
    };
  }, [feathers, detailOpen, candidateIdsInChart, candidateById]);

  const hasAny = timePoints.length > 0 || qualityPoints.length > 0;
  if (!hasAny) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', width: '100%', textAlign: 'center' }}>
        No time data yet
      </Typography>
    );
  }

  const openDetail = () => {
    setSelectedLevel(trainingLevel ?? availableLevels[0] ?? null);
    setDetailOpen(true);
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          alignItems: 'start',
          gap: 2,
          cursor: 'pointer',
        }}
        role="button"
        tabIndex={0}
        onClick={openDetail}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDetail();
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
          {timePoints.length > 0 ? (
            <StepChartWithAverage
              points={previewTimePoints}
              color="time"
              valueLabel={(y) => `${Math.round(y)} min`}
              average={previewTimeAvg}
              maxPoints={PREVIEW_POINTS}
              averageColor={isTimeAverageHigh ? '#d32f2f' : undefined}
              averagePrefix={
                isTimeAverageHigh ? <ErrorOutlineIcon sx={{ fontSize: 16, color: '#d32f2f' }} /> : undefined
              }
              averageSlotWidth={86}
            />
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              No time data yet
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
          {qualityPoints.length > 0 ? (
            <StepChartWithAverage
              points={previewQualityPoints}
              color="quality"
              valueLabel={(y) => y.toFixed(1)}
              average={previewQualityAvg}
              maxPoints={PREVIEW_POINTS}
              averageSuffix={<StarIcon sx={{ fontSize: 16, color: QUALITY_PURPLE }} />}
              averageSlotWidth={86}
            />
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              No quality data yet
            </Typography>
          )}
        </Box>
      </Box>
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6">Performance details</Typography>
              {activeLevel != null && (
                <Typography variant="body2" color="text.secondary">
                  Training level {activeLevel}
                </Typography>
              )}
            </Box>
            {availableLevels.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="performance-level-select-label">Training level</InputLabel>
                <Select
                  labelId="performance-level-select-label"
                  label="Training level"
                  value={activeLevel != null ? `${activeLevel}` : ''}
                  onChange={(event) => setSelectedLevel(Number(event.target.value))}
                >
                  {availableLevels.map((level) => (
                    <MenuItem key={level} value={`${level}`}>
                      Level {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailTimePoints.length > 0 ? (
            <DetailedStepChart
              title="Time spent per profile"
              points={detailTimePoints}
              color={TIME_PURPLE}
              valueLabel={(y) => `${Math.round(y)}m`}
              candidateById={candidateById}
              average={detailTimeAvg}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">No time data available.</Typography>
          )}
          {detailQualityPoints.length > 0 ? (
            <DetailedStepChart
              title="Review score per profile"
              points={detailQualityPoints}
              color={QUALITY_PURPLE}
              valueLabel={(y) => y.toFixed(1)}
              yDomain={{ min: 0, max: 5 }}
              yTickValues={[0, 1, 2, 3, 4, 5]}
              tooltipMode="quality"
              candidateById={candidateById}
              average={detailQualityAvg}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No quality data available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
